import type { Network } from '@ethersproject/networks';
import {
  FallbackProvider,
  StaticJsonRpcProvider,
} from '@ethersproject/providers';

import { buildRpcUrlList } from '@helpers/rpcUrls';

import {
  RPC_URL_AVALANCHE,
  RPC_URL_BINANCE,
  RPC_URL_ETHEREUM,
  RPC_URL_HOODI,
  RPC_URL_POLYGON,
  RPC_URL_SEPOLIA,
  RPC_URL_SMARTBCH,
  THIRDWEB_CLIENT_ID,
} from './constants';

export const RPC_CHAIN_ID = {
  ETHEREUM: 1,
  POLYGON: 137,
  BSC: 56,
  AVALANCHE: 43114,
  SMARTBCH: 10000,
  SEPOLIA: 11155111,
  HOODI: 560048,
} as const;

/**
 * Public endpoints used when the configured node (QuickNode) hits its daily
 * request limit (-32003) or is otherwise unavailable. These have no SLA and
 * their own rate limits — they are overflow, not a replacement for a paid plan.
 */
export const PUBLIC_RPC_URLS: Record<number, string[]> = {
  [RPC_CHAIN_ID.ETHEREUM]: [
    'https://eth.llamarpc.com',
    'https://ethereum-rpc.publicnode.com',
    'https://cloudflare-eth.com',
  ],
  [RPC_CHAIN_ID.POLYGON]: [
    'https://polygon-rpc.com',
    'https://polygon-bor-rpc.publicnode.com',
    'https://1rpc.io/matic',
  ],
  [RPC_CHAIN_ID.BSC]: [
    'https://bsc-dataseed.binance.org',
    'https://bsc-dataseed1.bnbchain.org',
    'https://1rpc.io/bnb',
  ],
  [RPC_CHAIN_ID.AVALANCHE]: [
    'https://api.avax.network/ext/bc/C/rpc',
    'https://avalanche-c-chain-rpc.publicnode.com',
  ],
  [RPC_CHAIN_ID.SMARTBCH]: [
    'https://smartbch.fountainhead.cash/mainnet',
    'https://smartbch.greyh.at',
  ],
  [RPC_CHAIN_ID.SEPOLIA]: [
    'https://rpc.sepolia.org',
    'https://ethereum-sepolia-rpc.publicnode.com',
  ],
  [RPC_CHAIN_ID.HOODI]: [
    'https://0xrpc.io/hoodi',
    'https://rpc.hoodi.ethpandaops.io',
  ],
};

const PRIMARY_RPC_URLS: Record<number, string | undefined> = {
  [RPC_CHAIN_ID.ETHEREUM]: RPC_URL_ETHEREUM,
  [RPC_CHAIN_ID.POLYGON]: RPC_URL_POLYGON,
  [RPC_CHAIN_ID.BSC]: RPC_URL_BINANCE,
  [RPC_CHAIN_ID.AVALANCHE]: RPC_URL_AVALANCHE,
  [RPC_CHAIN_ID.SMARTBCH]: RPC_URL_SMARTBCH,
  [RPC_CHAIN_ID.SEPOLIA]: RPC_URL_SEPOLIA,
  [RPC_CHAIN_ID.HOODI]: RPC_URL_HOODI,
};

const NETWORK_BY_CHAIN: Record<number, Network> = {
  [RPC_CHAIN_ID.ETHEREUM]: {
    name: 'homestead',
    chainId: RPC_CHAIN_ID.ETHEREUM,
  },
  [RPC_CHAIN_ID.POLYGON]: { name: 'matic', chainId: RPC_CHAIN_ID.POLYGON },
  [RPC_CHAIN_ID.BSC]: { name: 'bnb', chainId: RPC_CHAIN_ID.BSC },
  [RPC_CHAIN_ID.AVALANCHE]: {
    name: 'avalanche',
    chainId: RPC_CHAIN_ID.AVALANCHE,
  },
  [RPC_CHAIN_ID.SMARTBCH]: { name: 'smartbch', chainId: RPC_CHAIN_ID.SMARTBCH },
  [RPC_CHAIN_ID.SEPOLIA]: { name: 'sepolia', chainId: RPC_CHAIN_ID.SEPOLIA },
  [RPC_CHAIN_ID.HOODI]: { name: 'hoodi', chainId: RPC_CHAIN_ID.HOODI },
};

const CHAINS_WITHOUT_THIRDWEB_RPC = new Set<number>([
  RPC_CHAIN_ID.SMARTBCH,
  RPC_CHAIN_ID.HOODI,
]);

function thirdwebGatewayUrl(chainId: number): string | undefined {
  if (!THIRDWEB_CLIENT_ID || CHAINS_WITHOUT_THIRDWEB_RPC.has(chainId)) {
    return undefined;
  }
  return `https://${chainId}.rpc.thirdweb.com/${THIRDWEB_CLIENT_ID}`;
}

/**
 * Ordered RPC URLs for a chain: paid/env node first, then public overflow.
 * When no env URL is set, the thirdweb gateway (if configured) is tried
 * ahead of public endpoints — matching the previous DAppProvider behavior.
 */
export function getRpcUrls(chainId: number): string[] {
  const primary = PRIMARY_RPC_URLS[chainId];
  const fallbacks = PUBLIC_RPC_URLS[chainId] ?? [];
  if (primary) {
    return buildRpcUrlList(primary, fallbacks);
  }
  return buildRpcUrlList(thirdwebGatewayUrl(chainId), fallbacks);
}

export function getPreferredRpcUrl(chainId: number): string {
  const [preferred] = getRpcUrls(chainId);
  if (!preferred) {
    throw new Error(`No RPC URL configured for chain ${chainId}`);
  }
  return preferred;
}

function getProviderNetwork(chainId: number): Network {
  return NETWORK_BY_CHAIN[chainId] ?? { name: `chain-${chainId}`, chainId };
}

/**
 * Read-only provider for useDApp. Uses ethers FallbackProvider so a QuickNode
 * daily-limit / outage automatically retries on public RPCs (quorum 1 = first
 * success wins). A single URL skips the fallback wrapper.
 */
export function createReadOnlyProvider(
  chainId: number,
): FallbackProvider | StaticJsonRpcProvider {
  const urls = getRpcUrls(chainId);
  const network = getProviderNetwork(chainId);

  if (urls.length === 0) {
    throw new Error(`No RPC URL configured for chain ${chainId}`);
  }

  if (urls.length === 1) {
    return new StaticJsonRpcProvider(urls[0], network);
  }

  const configs = urls.map((url, index) => ({
    provider: new StaticJsonRpcProvider(url, network),
    priority: index + 1,
    weight: 1,
    // Fail over quickly on a hung paid node; public endpoints get a bit longer.
    stallTimeout: index === 0 ? 1500 : 2500,
  }));

  return new FallbackProvider(configs, 1);
}

export function createReadOnlyProviderFactory(chainId: number) {
  return () => createReadOnlyProvider(chainId);
}
